import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axiosInstance1 from '../../Services/app/loginservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';
import {
  Box, Typography, Paper, Chip, Button, IconButton, FormControl, InputLabel,
  Select, MenuItem, LinearProgress, CircularProgress, Tooltip, Divider, Switch,
  FormControlLabel,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';

// Tool Life Dashboard — operational view of every tool across every machine.
// Reads each machine's interlock_config (tool_life.tools) + live telemetry
// (tool_T<n>_health_pct/minutes/parts) and renders a wall-TV-friendly grid.
//
// Yantra palette:
//   primary blue  #2563eb / #1d4ed8     accent orange  #ea580c / #f97316
//   surface bg    #f8fafc               card           #fff
//   ok teal       #0f766e               warning amber  #ca8a04
//   alarm red     #b91c1c               critical       #991b1b

const ATTR_KEY = 'interlock_config';
const STATUS_COLORS = {
  halt:    { bg: '#fee2e2', fg: '#991b1b', bar: '#b91c1c', label: 'HALT' },
  warn:    { bg: '#fef9c3', fg: '#854d0e', bar: '#ca8a04', label: 'WARN' },
  ok:      { bg: '#d1fae5', fg: '#065f46', bar: '#0f766e', label: 'OK' },
  nodata:  { bg: '#f3f4f6', fg: '#6b7280', bar: '#9ca3af', label: 'NO DATA' },
};

// ── DEMO DATA ──────────────────────────────────────────────────────
// Realistic synthetic per-machine data so the dashboard can be previewed
// before the edge integration is live. Deterministic per machine name
// (same machine always gets the same fake state across refreshes).
const SAMPLE_TOOLS = [
  { tool_number: 1, name: 'Drill 6mm',              life_minutes: 180, life_parts: 500, wear_curve: { enabled: true, rate_um_per_100: 3,  axis: 'axial' } },
  { tool_number: 2, name: 'Reamer 6.2mm',           life_minutes: 120, life_parts: 400, wear_curve: { enabled: false, rate_um_per_100: 0,  axis: 'radial' } },
  { tool_number: 3, name: 'Roughing endmill 12mm',  life_minutes: 480, life_parts: 200, wear_curve: { enabled: true, rate_um_per_100: 12, axis: 'radial' } },
  { tool_number: 4, name: 'Finishing endmill 8mm',  life_minutes: 360, life_parts: 150, wear_curve: { enabled: true, rate_um_per_100: 4,  axis: 'radial' } },
  { tool_number: 5, name: 'Chamfer 4mm',            life_minutes: 240, life_parts: 300, wear_curve: { enabled: false, rate_um_per_100: 0,  axis: 'radial' } },
  { tool_number: 6, name: 'Boring bar 16mm',        life_minutes: 540, life_parts: 250, wear_curve: { enabled: true, rate_um_per_100: 8,  axis: 'radial' } },
  { tool_number: 7, name: 'Threading tap M8',       life_minutes:  90, life_parts: 600, wear_curve: { enabled: false, rate_um_per_100: 0,  axis: 'axial' } },
  { tool_number: 8, name: 'Face mill 50mm',         life_minutes: 600, life_parts: 180, wear_curve: { enabled: true, rate_um_per_100: 6,  axis: 'axial' } },
];

const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// Generate synthetic state for one machine. Mix of healthy / warning / halt
// so every section of the dashboard has visible data.
const demoMachineData = (machine) => {
  const h = hashStr(machine.name || 'x');
  const numTools = 3 + (h % 4); // 3, 4, 5, or 6 tools
  const tools = SAMPLE_TOOLS.slice(0, numTools).map(t => ({
    ...t, enabled: true, warning_pct: 80, halt_pct: 100, halt_machine: true,
  }));
  const activeTool = tools[h % tools.length].tool_number;
  const live = {};
  tools.forEach((t, i) => {
    const seed = (h + i * 97) >>> 0;
    const bucket = seed % 100;
    let pct;
    if (bucket < 6)        pct = 100 + (seed % 4);          // 6% halt
    else if (bucket < 22)  pct = 80 + ((seed >> 3) % 19);   // 16% warning
    else                   pct = ((seed >> 5) % 75);        // 78% healthy
    const minutes = Math.round((pct / 100) * t.life_minutes * 10) / 10;
    const parts = Math.round((pct / 100) * t.life_parts);
    live[t.tool_number] = { health_pct: pct, minutes, parts };
  });
  return { name: machine.name, tools, live, activeTool, error: null };
};

const ToolDashboard = () => {
  const customerId = localStorage.getItem('CustomerID');
  const SERVER = (window._env_?.SERVER_URL || '').replace(/\/$/, '');
  const cid = cleanCustomerId(customerId);

  const [devices, setDevices] = useState([]);
  const [machineFilter, setMachineFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  // perMachine: { [deviceId]: { name, tools: [], live: {...by tool_number}, error } }
  const [perMachine, setPerMachine] = useState({});
  const [demoMode, setDemoMode] = useState(false);
  const [demoExplicit, setDemoExplicit] = useState(false); // user manually flipped it
  const [firstRealLoadDone, setFirstRealLoadDone] = useState(false);
  const [showHealthy, setShowHealthy] = useState(false); // healthy collapsed by default

  // 1. Load device list
  useEffect(() => {
    if (!cid) return;
    setLoading(true);
    axiosInstance1
      .get(`${SERVER}/api/customer/${cid}/devices`, { params: { pageSize: 500, page: 0 } })
      .then(r => {
        const list = (r.data?.data || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDevices(list);
      })
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, [SERVER, cid]);

  // 2. Refresh tool data for every (or one) machine
  const refresh = useCallback(async () => {
    if (!devices.length) return;
    setRefreshing(true);
    const targets = machineFilter === 'all' ? devices : devices.filter(d => d.id?.id === machineFilter);
    const next = { ...perMachine };

    await Promise.all(targets.map(async (d) => {
      const did = d.id?.id;
      try {
        // Pull the interlock_config attribute (for tool list)
        const cfgR = await axiosInstance1.get(
          `${SERVER}/api/plugins/telemetry/DEVICE/${did}/values/attributes`,
          { params: { keys: ATTR_KEY } }
        );
        const cfg = (cfgR.data || []).find(a => a.key === ATTR_KEY)?.value || {};
        const tools = cfg.tool_life?.enabled ? (cfg.tool_life.tools || []).filter(t => t.enabled) : [];

        // Per-tool live telemetry + the currently active tool
        const keys = ['live_tool_number'];
        tools.forEach(t => {
          const n = String(t.tool_number).padStart(2, '0');
          keys.push(`tool_T${n}_health_pct`, `tool_T${n}_minutes`, `tool_T${n}_parts`);
        });
        let live = {};
        let activeTool = null;
        if (tools.length) {
          const liveR = await axiosInstance1.get(
            `${SERVER}/api/plugins/telemetry/DEVICE/${did}/values/timeseries`,
            { params: { keys: keys.join(','), useStrictDataTypes: false } }
          );
          const data = liveR.data || {};
          activeTool = data.live_tool_number?.[0]?.value;
          tools.forEach(t => {
            const n = String(t.tool_number).padStart(2, '0');
            live[t.tool_number] = {
              health_pct: Number(data[`tool_T${n}_health_pct`]?.[0]?.value ?? NaN),
              minutes: Number(data[`tool_T${n}_minutes`]?.[0]?.value ?? NaN),
              parts: Number(data[`tool_T${n}_parts`]?.[0]?.value ?? NaN),
            };
          });
        }
        next[did] = { name: d.name, tools, live, activeTool, error: null };
      } catch (e) {
        next[did] = { name: d.name, tools: [], live: {}, error: e?.message || 'fetch error' };
      }
    }));
    setPerMachine(next);
    setLastRefreshAt(Date.now());
    setRefreshing(false);

    // Auto-enable demo on the first real load IF no machines have any
    // configured + enabled tools yet (so the page never opens empty).
    // User-explicit toggle wins — we never override after they touched it.
    if (!firstRealLoadDone) {
      const anyRealTools = Object.values(next).some(m => (m.tools || []).length > 0);
      if (!anyRealTools && !demoExplicit) setDemoMode(true);
      setFirstRealLoadDone(true);
    }
  }, [devices, machineFilter, SERVER, perMachine, firstRealLoadDone, demoExplicit]);

  // initial + filter-change refresh
  useEffect(() => { if (devices.length && !demoMode) refresh(); /* eslint-disable-line */ }, [devices, machineFilter, demoMode]);

  // auto-refresh every 15s (real data only)
  useEffect(() => {
    if (!autoRefresh || demoMode) return;
    const id = setInterval(() => refresh(), 15000);
    return () => clearInterval(id);
  }, [autoRefresh, demoMode, refresh]);

  // Demo mode → synthesise per-machine data instead of fetching
  useEffect(() => {
    if (!demoMode) return;
    const targets = machineFilter === 'all' ? devices : devices.filter(d => d.id?.id === machineFilter);
    const synth = {};
    targets.forEach(d => { synth[d.id?.id] = demoMachineData(d); });
    setPerMachine(synth);
    setLastRefreshAt(Date.now());
  }, [demoMode, devices, machineFilter]);

  // 3. Classify every (machine, tool) into halt / warn / ok / nodata buckets
  // Also compute Phase-A wear-offset suggestion if wear_curve is enabled.
  const allRows = useMemo(() => {
    const rows = [];
    Object.entries(perMachine).forEach(([did, m]) => {
      m.tools.forEach(t => {
        const live = m.live?.[t.tool_number] || {};
        const isActive = Number(m.activeTool) === Number(t.tool_number);
        const pct = Number(live.health_pct);
        const hasData = !isNaN(pct) && pct !== null;
        let status;
        if (!hasData) status = 'nodata';
        else if (pct >= (t.halt_pct ?? 100)) status = 'halt';
        else if (pct >= (t.warning_pct ?? 80)) status = 'warn';
        else status = 'ok';
        // Wear-offset suggestion (Phase A — informational only)
        let wearSuggestion = null;
        const wc = t.wear_curve;
        const parts = Number(live.parts);
        if (wc?.enabled && wc.rate_um_per_100 > 0 && !isNaN(parts) && parts > 0) {
          const um = (parts / 100) * wc.rate_um_per_100;
          wearSuggestion = {
            um: um.toFixed(1),
            mm: (um / 1000).toFixed(4),
            axis: wc.axis || 'radial',
            notes: wc.notes || '',
          };
        }
        rows.push({
          deviceId: did, machineName: m.name,
          tool: t, live, isActive, healthPct: hasData ? pct : null, status, wearSuggestion,
        });
      });
    });
    return rows;
  }, [perMachine]);

  const buckets = useMemo(() => ({
    halt:   allRows.filter(r => r.status === 'halt'),
    warn:   allRows.filter(r => r.status === 'warn'),
    ok:     allRows.filter(r => r.status === 'ok'),
    nodata: allRows.filter(r => r.status === 'nodata'),
  }), [allRows]);

  // Per-machine summary (worst-status of any tool on the machine)
  const machineCards = useMemo(() => {
    const order = { halt: 0, warn: 1, ok: 2, nodata: 3 };
    return Object.entries(perMachine).map(([did, m]) => {
      const rows = allRows.filter(r => r.deviceId === did);
      const worst = rows.reduce((acc, r) => (order[r.status] < order[acc] ? r.status : acc), 'nodata');
      const activeRow = rows.find(r => r.isActive);
      return { did, name: m.name, worst, activeRow, count: rows.length };
    }).sort((a, b) => order[a.worst] - order[b.worst] || (a.name || '').localeCompare(b.name || ''));
  }, [perMachine, allRows]);

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 80px)' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
            🔧 Tool Life Dashboard
          </Typography>
          {demoMode && (
            <Tooltip title={demoExplicit ? 'You enabled demo mode from the toolbar. Turn it off to see live data.' : 'Auto-enabled because no tools are configured yet on any machine. Set up tools on the Interlock Config page, then this will switch off automatically.'}>
              <Chip
                label={demoExplicit ? 'DEMO DATA — not real machine state' : 'DEMO DATA (auto-enabled — no real tools configured yet)'}
                size="small"
                sx={{ bgcolor: '#fed7aa', color: '#9a3412', fontWeight: 700, fontSize: 11 }}
              />
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
          Real-time tool health across every machine. Action list prioritises tools that need a change.
        </Typography>
      </Box>

      {/* Top bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'var(--y-bg, #fff)' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Machine</InputLabel>
            <Select value={machineFilter} label="Machine" onChange={(e) => setMachineFilter(e.target.value)}>
              <MenuItem value="all">All machines ({devices.length})</MenuItem>
              {devices.map(d => (
                <MenuItem key={d.id?.id} value={d.id?.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          {/* Status chips */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={`🔴 ${buckets.halt.length} halt`}
              sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
            <Chip size="small" label={`🟡 ${buckets.warn.length} warn`}
              sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 700 }} />
            <Chip size="small" label={`🟢 ${buckets.ok.length} ok`}
              sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700 }} />
            <Chip size="small" label={`⚪ ${buckets.nodata.length} no data`}
              sx={{ bgcolor: '#f3f4f6', color: '#6b7280', fontWeight: 700 }} />
          </Box>

          <Tooltip title="Preview the dashboard with realistic synthetic data per machine. Turn off to see real (live edge) data.">
            <FormControlLabel
              control={<Switch checked={demoMode} onChange={(e) => { setDemoExplicit(true); setDemoMode(e.target.checked); }} sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ea580c' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ea580c' },
              }} />}
              label={<Typography sx={{ fontSize: 12, color: demoMode ? '#9a3412' : 'inherit', fontWeight: demoMode ? 700 : 400 }}>
                🎨 Demo data
              </Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} disabled={demoMode} sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2563eb' },
            }} />}
            label={<Typography sx={{ fontSize: 12, color: demoMode ? '#9ca3af' : 'inherit' }}>Auto-refresh 15s</Typography>}
            sx={{ m: 0 }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={14} /> : <Refresh fontSize="small" />}
            onClick={refresh}
            disabled={refreshing}
            sx={{ textTransform: 'none', color: '#2563eb', borderColor: '#93c5fd' }}
          >
            Refresh now
          </Button>
        </Box>
        {lastRefreshAt && (
          <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
            Last refreshed {new Date(lastRefreshAt).toLocaleTimeString()}
          </Typography>
        )}
      </Paper>

      {loading ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'var(--y-bg, #fff)' }}>
          <CircularProgress sx={{ color: '#ea580c' }} />
        </Paper>
      ) : devices.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: '#6b7280', bgcolor: 'var(--y-bg, #fff)' }}>
          No machines available.
        </Paper>
      ) : allRows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: '#6b7280', bgcolor: 'var(--y-bg, #fff)' }}>
          <Typography sx={{ fontSize: 14 }}>No tools configured on any machine yet.</Typography>
          <Typography sx={{ fontSize: 12, mt: 1 }}>
            Go to Master → Interlock Config → Tool Life tab to set up tools per machine.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* MACHINE TILES — richer: tool name + cutting badge */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5, mb: 3 }}>
            {machineCards.map(mc => {
              const c = STATUS_COLORS[mc.worst];
              return (
                <Paper key={mc.did} variant="outlined"
                  sx={{ p: 1.2, bgcolor: c.bg, borderColor: mc.worst === 'halt' ? c.bar : '#e5e7eb',
                        borderLeft: `4px solid ${c.bar}`, cursor: 'pointer',
                        '&:hover': { boxShadow: 2 } }}
                  onClick={() => setMachineFilter(mc.did)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>{mc.name}</Typography>
                    {mc.activeRow?.isActive && (
                      <Chip size="small" label="CUTTING" sx={{ bgcolor: '#fff7ed', color: '#9a3412', fontSize: 8, height: 14, fontWeight: 700 }} />
                    )}
                  </Box>
                  {mc.activeRow ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.3 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: c.fg }}>
                          T{String(mc.activeRow.tool.tool_number).padStart(2, '0')}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: c.fg, fontWeight: 600,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {mc.activeRow.tool.name || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: c.bar }}>
                          {mc.activeRow.healthPct !== null ? `${mc.activeRow.healthPct.toFixed(0)}%` : '—'}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: c.fg, fontWeight: 700, mt: 0.3 }}>
                        {c.label}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                      {mc.count > 0 ? `${mc.count} tool${mc.count === 1 ? '' : 's'} configured` : 'no tools'}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Box>

          {/* ACTION LIST — slim rows, urgency-sorted, Healthy collapsed by default */}
          {['halt', 'warn', 'ok', 'nodata'].map(b => {
            const rows = buckets[b];
            if (!rows.length) return null;
            const c = STATUS_COLORS[b];
            const header = {
              halt:   '🔴 NEEDS CHANGE NOW',
              warn:   '🟡 CHANGE SOON',
              ok:     '🟢 HEALTHY',
              nodata: '⚪ NO DATA YET',
            }[b];
            // Healthy is collapsed by default
            const isCollapsible = (b === 'ok' || b === 'nodata');
            const isExpanded = !isCollapsible || (b === 'ok' && showHealthy) || (b === 'nodata' && showHealthy);
            return (
              <Box key={b} sx={{ mb: 2 }}>
                <Box
                  onClick={isCollapsible ? () => setShowHealthy(s => !s) : undefined}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                        cursor: isCollapsible ? 'pointer' : 'default',
                        userSelect: 'none' }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.fg }}>
                    {header} ({rows.length})
                  </Typography>
                  {isCollapsible && (
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                      {isExpanded ? '▼ hide' : '▶ show'}
                    </Typography>
                  )}
                </Box>
                {isExpanded && (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: b === 'ok' || b === 'nodata'
                      ? 'repeat(auto-fit, minmax(380px, 1fr))'  // healthy: 3-4 cols on wide screens
                      : 'repeat(auto-fit, minmax(520px, 1fr))', // halt/warn: 2-3 cols
                    gap: 0.5,
                  }}>
                    {rows.map((r, i) => (
                      <Paper key={`${r.deviceId}-${r.tool.tool_number}-${i}`} variant="outlined"
                        sx={{ px: 1.2, py: 0.5, bgcolor: 'var(--y-bg, #fff)', borderLeft: `3px solid ${c.bar}`,
                              display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                      >
                        <Chip size="small" label={`T${String(r.tool.tool_number).padStart(2, '0')}`}
                          sx={{ bgcolor: r.isActive ? '#ea580c' : '#2563eb', color: '#fff', fontWeight: 700, fontSize: 10, height: 18, minWidth: 36 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap' }}>
                          {r.machineName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#374151', flex: 1, minWidth: 0,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.tool.name || '(unnamed)'}
                        </Typography>
                        {r.isActive && (
                          <Chip size="small" label="CUT" sx={{ bgcolor: '#fff7ed', color: '#9a3412', fontSize: 9, height: 14, fontWeight: 700, minWidth: 28 }} />
                        )}
                        <Tooltip title={`${!isNaN(r.live.minutes) ? `${r.live.minutes.toFixed(0)}/${r.tool.life_minutes} min` : 'no min data'}\n${!isNaN(r.live.parts) ? `${r.live.parts.toFixed(0)}/${r.tool.life_parts} parts` : 'no parts data'}`}>
                          <Typography sx={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap', cursor: 'help' }}>
                            {!isNaN(r.live.parts) ? `${r.live.parts.toFixed(0)}p` : '—'}
                          </Typography>
                        </Tooltip>
                        {r.wearSuggestion && (
                          <Tooltip title={`Suggested ${r.wearSuggestion.axis} offset: ${r.wearSuggestion.um}µm (${r.wearSuggestion.mm}mm).\nPredicted from ${r.tool.wear_curve.rate_um_per_100}µm per 100 parts. Phase A — informational only.${r.wearSuggestion.notes ? '\nNotes: ' + r.wearSuggestion.notes : ''}`}>
                            <Chip
                              size="small"
                              label={`💡${r.wearSuggestion.um}µm`}
                              sx={{ bgcolor: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa',
                                    fontWeight: 700, fontSize: 10, height: 18 }}
                            />
                          </Tooltip>
                        )}
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: c.bar, minWidth: 48, textAlign: 'right' }}>
                          {r.healthPct !== null ? `${r.healthPct.toFixed(0)}%` : '—'}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default ToolDashboard;
